import { useState, useEffect, useLayoutEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/LanguageContext';
import { Button } from './ui/button';
import { X, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const DESKTOP_STEPS = ['home', 'explore', 'messages', 'lives', 'wallet', 'search', 'notifications', 'wellness', 'profile'];
const MOBILE_STEPS = ['home', 'explore', 'lives', 'messages', 'profile'];
const isMobile = () => window.innerWidth < 768;
const NAV_STEPS = ['home', 'explore', 'messages', 'lives', 'wallet'];
const PUBLIC_PATHS = ['/', '/login', '/register', '/auth/callback'];
const PAD = 6;
const POPUP_W = 300;

const findTarget = (step) => {
  const visible = (el) => { const r = el.getBoundingClientRect(); return r.width > 0 && r.height > 0; };
  const navEl = isMobile() && document.querySelector(`[data-testid="mobile-bottom-nav"] [data-tour="${step}"]`);
  if (navEl && visible(navEl)) return navEl;
  const el = [...document.querySelectorAll(`[data-tour="${step}"]`)].find(visible);
  if (el) return el;
  if (NAV_STEPS.includes(step)) return [...document.querySelectorAll('[data-tour="mobile-menu"]')].find(visible) || null;
  return null;
};

const popupPosition = (rect) => {
  const vw = window.innerWidth, vh = window.innerHeight;
  const width = Math.min(POPUP_W, vw - 24);
  if (!rect) return { width, left: (vw - width) / 2, top: vh / 2 - 100 };
  const left = Math.min(Math.max(12, rect.left + rect.width / 2 - width / 2), vw - width - 12);
  const below = rect.bottom + PAD + 12;
  if (below + 260 < vh) return { width, left, top: below };
  return { width, left, bottom: vh - (rect.top - PAD - 12) };
};

export const OnboardingTour = () => {
  const { user, updateUser } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const active = !!user?.onboarding_active && !PUBLIC_PATHS.includes(location.pathname);
  const [STEPS, setSteps] = useState(() => (isMobile() ? MOBILE_STEPS : DESKTOP_STEPS));
  const step = STEPS[index] || STEPS[0];

  useEffect(() => {
    const onResize = () => { setSteps(isMobile() ? MOBILE_STEPS : DESKTOP_STEPS); setIndex(0); };
    const mq = window.matchMedia('(max-width: 767px)');
    mq.addEventListener('change', onResize);
    return () => mq.removeEventListener('change', onResize);
  }, []);

  const measure = useCallback(() => {
    const el = findTarget(step);
    const r = el ? el.getBoundingClientRect() : null;
    setRect(prev => (prev && r && prev.top === r.top && prev.left === r.left && prev.width === r.width) || (!prev && !r) ? prev : r);
  }, [step]);

  useLayoutEffect(() => {
    if (!active) return;
    findTarget(step)?.scrollIntoView({ block: 'center', inline: 'nearest' });
    measure();
    const id = setInterval(measure, 400);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => { clearInterval(id); window.removeEventListener('resize', measure); window.removeEventListener('scroll', measure, true); };
  }, [active, step, measure, location.pathname]);

  const close = useCallback(() => {
    updateUser({ onboarding_active: false });
    axios.post(`${API}/onboarding/dismiss`, {}).catch(() => {});
  }, [updateUser]);

  useEffect(() => {
    if (!active) setIndex(0);
  }, [active]);

  useEffect(() => {
    if (!active) return;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowRight') setIndex(i => Math.min(i + 1, STEPS.length - 1));
      if (e.key === 'ArrowLeft') setIndex(i => Math.max(i - 1, 0));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, close, STEPS.length]);

  if (!active) return null;
  const isLast = index === STEPS.length - 1;
  const pos = popupPosition(rect);

  return (
    <div className="fixed inset-0 z-[300]" role="dialog" aria-modal="true" data-testid="onboarding-tour">
      <style>{'[data-sonner-toaster]{visibility:hidden}'}</style>
      {rect ? (
        <div
          className="absolute rounded-2xl ring-2 ring-[var(--papo-gold)] transition-[top,left,width,height] duration-300 ease-out pointer-events-none"
          style={{ top: rect.top - PAD, left: rect.left - PAD, width: rect.width + PAD * 2, height: rect.height + PAD * 2, boxShadow: '0 0 0 9999px rgba(0,0,0,0.68), 0 0 24px 4px rgba(245, 183, 0, 0.55)' }}
          data-testid="onboarding-highlight"
        />
      ) : (
        <div className="absolute inset-0 bg-black/70" />
      )}
      <div
        key={step}
        className="absolute bg-[var(--card-bg)] border border-white/10 rounded-2xl p-4 shadow-2xl animate-reaction-pop transition-[top,left,bottom] duration-300"
        style={pos}
        data-testid={`onboarding-step-${step}`}
      >
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 text-[var(--papo-gold)]">
            <Sparkles className="w-4 h-4" />
            <span className="text-xs font-semibold" data-testid="onboarding-counter">{index + 1} / {STEPS.length}</span>
          </div>
          <button onClick={close} className="p-1 rounded-full text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--elevated-bg)] transition-colors" aria-label={t('onboarding.skip')} data-testid="onboarding-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <h3 className="font-bold text-[var(--text-primary)] font-['Nunito'] mb-1" data-testid="onboarding-title">{t(`onboarding.${step}.title`)}</h3>
        <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-3">{t(`onboarding.${step}.desc`)}</p>
        <div className="h-1 rounded-full bg-[var(--elevated-bg)] overflow-hidden mb-3">
          <div className="h-full gradient-warm transition-[width] duration-300" style={{ width: `${((index + 1) / STEPS.length) * 100}%` }} data-testid="onboarding-progress" />
        </div>
        <div className="flex items-center justify-between gap-2">
          <button onClick={close} className="text-xs font-semibold text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors" data-testid="onboarding-skip">
            {t('onboarding.skip')}
          </button>
          <div className="flex gap-2">
            {index > 0 && (
              <Button size="sm" variant="ghost" onClick={() => setIndex(index - 1)} className="rounded-full h-8 px-3" data-testid="onboarding-prev">
                <ChevronLeft className="w-4 h-4" />{t('onboarding.prev')}
              </Button>
            )}
            <Button size="sm" onClick={() => (isLast ? close() : setIndex(index + 1))} className="rounded-full h-8 px-4 gradient-warm text-white" data-testid="onboarding-next">
              {isLast ? t('onboarding.finish') : t('onboarding.next')}
              {!isLast && <ChevronRight className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
